/**
 * GTX motion (owner 13/10, #31). Progressive enhancement only: without this script nothing is hidden.
 * - Adds .gtx-motion to <html> so the CSS motion rules in gtx-custom.css apply.
 * - Reveal: tags only the #MainContent sections that start fully below the first screen (never the first
 *   section, so never the hero, the H1 or the LCP image) and reveals each once when it scrolls into view.
 * - Open/expand: marks a <details>, a tab set or the fitment table for one short animation after a click
 *   (capture phase, so the state is read before the component's own click handler changes it).
 * - Hero: adds .gtx-motion-loaded after window load for the one-off slow push-in.
 * Does nothing when the user prefers reduced motion or IntersectionObserver is missing.
 */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduce.matches || !('IntersectionObserver' in window)) return;

  const root = document.documentElement;
  if (root.classList.contains('shopify-design-mode')) return;
  root.classList.add('gtx-motion');

  /** @param {Element} element @param {string} className @param {number} ms */
  const flash = (element, className, ms) => {
    element.classList.add(className);
    window.setTimeout(() => element.classList.remove(className), ms);
  };

  const initReveal = () => {
    const sections = Array.from(document.querySelectorAll('#MainContent > .shopify-section')).slice(1);
    const fold = window.innerHeight;
    const targets = sections.filter((section) => section.getBoundingClientRect().top >= fold);
    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      },
      // A section the page has jumped past (End key, anchor link) counts as seen too, so it never stays hidden.
      { rootMargin: '100000px 0px -8% 0px' }
    );
    for (const section of targets) {
      section.classList.add('gtx-reveal');
      observer.observe(section);
    }
  };

  document.addEventListener('click', (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!(target instanceof Element)) return;

    const summary = target.closest('#MainContent summary');
    const details = summary?.parentElement;
    if (details instanceof HTMLDetailsElement && !details.open) flash(details, 'gtx-opening', 400);

    const tab = target.closest('.tabs-component-panel-trigger');
    const tabs = tab?.closest('tabs-component');
    if (tabs) flash(tabs, 'gtx-tab-switch', 400);

    const toggle = target.closest('.spo-fit__toggle');
    const table = toggle?.closest('spo-fitment-table');
    if (table?.classList.contains('spo-fit--collapsed')) flash(table, 'gtx-expanding', 400);
  }, true);

  // PSI 17/10 (forced reflow): initReveal reads layout (getBoundingClientRect, innerHeight). Run it after the first
  // frame has painted (layout already clean, so no forced reflow) instead of during script evaluation. Sections it
  // tags are fully below the first screen, so nothing visible moves.
  const start = () => {
    window.requestAnimationFrame(() => window.setTimeout(initReveal, 0));
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  const onLoaded = () => window.requestAnimationFrame(() => root.classList.add('gtx-motion-loaded'));
  if (document.readyState === 'complete') onLoaded();
  else window.addEventListener('load', onLoaded, { once: true });
})();
